/**
 * Finding a child on the roster by saying their name.
 *
 * The slow part of a dismissal line is not the tap that records a child — it is reaching
 * them. A parent gives a name at the gate and the teacher scrolls a hundred rows across
 * school and grade tabs to get to it. This does that part, and only that part: say a name,
 * get the handful of children it could have been, tap the one you meant.
 *
 * It deliberately understands nothing else. There is no "check in Emma" grammar, because a
 * recogniser that mishears a verb would act on the wrong thing, and because the rows below
 * already carry the right buttons for each child's current state. Those buttons are the
 * roster's own — passed in, not rebuilt — so a check-out still opens the sheet that asks who
 * collected the child, a Flex freeze still blocks, and voice cannot route around either.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { Mic, MicOff, X } from 'lucide-react';
import { suggestStudents, type MatchableName, type NameScore } from './voiceNameMatch';
import { useSpeechRecognition } from './useSpeechRecognition';
import { createPortal } from 'react-dom';
import { AttendanceStatusBadge } from '../../components/attendance/AttendanceStatusBadge';
import { StudentAvatar } from '../../components/StudentAvatar';
import type { SwipeAction } from '../components/SwipeableRow';
import type { RosterEntry } from '../useStaffRosterEntries';
import type { AttendanceStatus } from '../../types/attendance';
import { formatGradeLabel, UNKNOWN_GRADE } from '../../utils/rosterGrouping';
import { shortProgramLabel } from '../../utils/programLabel';
import { useVoiceLearning, useVoiceLookupLogger } from './useVoiceLearning';
import { AfterSchoolBadge } from '../components/AfterSchoolBadge';

interface Props {
  /** Every child in this group, across every tab — a name is searched for, not navigated to. */
  entries: readonly RosterEntry[];
  statusFor: (entry: RosterEntry) => AttendanceStatus;
  /** The roster's own buttons for this child, in this state. */
  actionsFor: (entry: RosterEntry) => SwipeAction[];
  /** The child's headshot, already signed by the roster. */
  photoUrlFor?: (entry: RosterEntry) => string | null;
  /**
   * Who is looking, for the feedback log. Absent means nothing is recorded and nothing is
   * learned — the matcher still works, it just stops getting better. That is the state in
   * tests and anywhere this panel is mounted without a signed-in teacher.
   */
  staffEmail?: string | null;
  /** Which roster this is, so a correction can be attributed to the class it came from. */
  band?: string | null;
  /**
   * Whether to say where each child goes when the class ends. True only on an enrichment
   * roster — see `isEnrichmentRoster`. The shortlist is where a teacher acts on a name
   * without ever seeing the row, so the thing the row would have told them has to be here.
   */
  showAfterSchoolBadge?: boolean;
}

/**
 * "TK · Bright Horizon · Chess" — what tells two rows of the shortlist apart.
 *
 * The program is in there because a shortlist is where names collide by definition, and one
 * child enrolled in two programs appears twice with identical grade and school. Without it
 * those two rows are indistinguishable, and they are the two rows most likely to be shown
 * together.
 */
function describe(entry: RosterEntry): string {
  return (
    [
      formatGradeLabel(entry.grade?.trim() || UNKNOWN_GRADE),
      entry.school,
      shortProgramLabel(entry.programName),
    ]
      .filter(Boolean)
      .join(' · ') || 'On this roster'
  );
}

export function VoiceAttendance({
  entries,
  statusFor,
  actionsFor,
  photoUrlFor,
  staffEmail = null,
  band = null,
  showAfterSchoolBadge = false,
}: Props) {
  const [open, setOpen] = useState(false);
  /** The last utterance the recogniser committed to. */
  const [heard, setHeard] = useState<string | null>(null);
  /** The last thing recorded, kept as a one-line receipt while the next name is spoken. */
  const [receipt, setReceipt] = useState<string | null>(null);
  /**
   * The shortlist on screen, once the recogniser has committed to a name that matched somebody.
   *
   * Held, rather than recomputed every render from whatever the microphone last picked up.
   * A dismissal line is a loud room: a parent talking at the gate, a chair, the teacher's own
   * "just a second" all reach the recogniser, and each one used to redraw this list — usually
   * to nothing, since noise matches nobody. The teacher's word for it was that the child they
   * were about to tap disappeared, which is exactly what happened: the finger was already
   * moving and the row was gone.
   *
   * So once a shortlist exists it stands until something deliberate replaces it — another
   * name that actually matches somebody, a button, or the panel closing.
   */
  const [shortlist, setShortlist] = useState<{ transcript: string; ranked: NameScore[] } | null>(
    null,
  );
  /** Set when a committed utterance matched nobody while a shortlist was standing. */
  const [missed, setMissed] = useState(false);

  const byEnrollment = useMemo(
    () => new Map(entries.map(entry => [entry.enrollmentId, entry])),
    [entries],
  );

  const studentIds = useMemo(() => entries.map(entry => entry.studentId), [entries]);
  const { aliases, lastPicked } = useVoiceLearning(staffEmail, studentIds);
  const logLookup = useVoiceLookupLogger(staffEmail, band);

  const matchable = useMemo<MatchableName[]>(
    () =>
      entries.map(entry => ({
        key: entry.enrollmentId,
        name: entry.studentName,
        // Keyed by student, applied per enrollment: a child in two programs is two rows on
        // this roster and the same child to a parent at the gate, so an alias taught against
        // one of their rows has to reach the other.
        aliases: aliases.get(entry.studentId) ?? null,
        lastPickedAt: lastPicked.get(entry.studentId) ?? null,
      })),
    [entries, aliases, lastPicked],
  );

  /**
   * The lookup that has not been resolved yet.
   *
   * A lookup is only worth recording once its outcome is known, and the outcome arrives from
   * three different directions: the teacher taps a child, or says the next name, or closes
   * the panel. All three flush this; the logger dedupes, because they can race.
   *
   * A flush with no child is not a wasted row. "Said a name, was offered these five, tapped
   * none of them" is the single most informative thing this feature can record — it is the
   * matcher being wrong in the way that no correction will ever teach us about.
   */
  const pending = useRef<{ transcript: string; ranked: NameScore[] } | null>(null);

  const flush = useCallback(
    (chosen: RosterEntry | null) => {
      const lookup = pending.current;
      if (!lookup) return;
      pending.current = null;
      logLookup({
        transcript: lookup.transcript,
        alternatives: [],
        ranked: lookup.ranked,
        chosenEnrollmentId: chosen?.enrollmentId ?? null,
        chosenStudentId: chosen?.studentId ?? null,
        rosterSize: entries.length,
      });
    },
    [logLookup, entries.length],
  );

  /**
   * Stray sounds absorbed since the standing shortlist was drawn.
   *
   * A ref rather than state because nothing renders from the count itself, and because it has
   * to survive the render that `setMissed` schedules.
   */
  const unmatched = useRef(0);

  const handleUtterance = ({ transcript }: { transcript: string }) => {
    const ranked = suggestStudents(transcript, matchable);

    // A shortlist on screen is a question the teacher is in the middle of answering, and a
    // committed utterance that matches nobody is almost always the room rather than a name.
    // Absorb it: say so, and leave the list where it is.
    //
    // Only the first one, though. Twice in a row is not the room — it is a name this matcher
    // cannot place, and the teacher has to be told that rather than left tapping at an answer
    // to a question they stopped asking. The second one falls through and clears the list,
    // reaching the "nobody sounds like that" line below.
    if (ranked.length === 0 && shortlist && unmatched.current === 0) {
      unmatched.current += 1;
      setMissed(true);
      return;
    }

    // The previous name went unanswered — record that before it is overwritten.
    flush(null);
    unmatched.current = 0;
    setMissed(false);
    setHeard(transcript);
    setShortlist(ranked.length > 0 ? { transcript, ranked } : null);
    setReceipt(null);

    // Only a committed utterance is worth logging, and only one this panel acted on. Interim
    // text changes several times a second while the teacher is still talking, and an absorbed
    // stray sound is not a lookup the matcher failed — recording it as one would teach the
    // wrong lesson from the most informative row this feature writes.
    pending.current = { transcript, ranked };
  };

  const { supported, listening, interim, error, start, stop } = useSpeechRecognition({
    onUtterance: handleUtterance,
  });

  /**
   * What the teacher is saying, preferring the words still in flight — until there is an
   * answer on screen.
   *
   * A recogniser does not commit to an utterance until it hears a pause, which is a second
   * or more after the name has been said. Matching only on the committed text left the panel
   * showing the name in grey with nothing under it — looking broken at exactly the moment it
   * had already worked out the answer. Interim text is good enough to search on, because
   * searching is all this does: nothing is recorded until a button is pressed.
   *
   * That argument is about getting the *first* shortlist up fast, and it stops applying the
   * moment one exists. After that, interim words are the next name arriving — or the room —
   * and neither is a reason to take back an answer the teacher is reaching for. So a standing
   * shortlist outranks them, and is replaced only when the next name commits.
   */
  const spoken = (shortlist ? null : interim) || heard;

  const suggestions = useMemo<NameScore[]>(
    () => (shortlist ? shortlist.ranked : spoken ? suggestStudents(spoken, matchable) : []),
    [shortlist, spoken, matchable],
  );

  const openPanel = () => {
    setHeard(null);
    setShortlist(null);
    setMissed(false);
    unmatched.current = 0;
    setReceipt(null);
    setOpen(true);
    start();
  };

  const closePanel = () => {
    flush(null);
    stop();
    setShortlist(null);
    setMissed(false);
    unmatched.current = 0;
    setOpen(false);
  };

  /**
   * Record what the teacher tapped, and clear the list.
   *
   * The panel keeps listening. A dismissal line is one child after another, and making the
   * teacher reopen this between each one would give back the time it just saved.
   */
  const runAction = (entry: RosterEntry, action: SwipeAction) => {
    flush(entry);
    action.onSelect();
    setReceipt(`${entry.studentName} — ${action.label}`);
    setHeard(null);
    setShortlist(null);
    setMissed(false);
    unmatched.current = 0;
  };

  // Both the button and the panel are `position: fixed`, and both go to `document.body`
  // rather than into the roster, for the reason spelled out under `MOBILE_CHROME_ON_BODY`
  // in `StaffLayout`: the page renders inside pull-to-refresh's `overflow: hidden` wrappers,
  // and on iOS a clip in the ancestry is enough for WebKit to anchor a fixed element to the
  // document instead of the viewport. The button drifted up the screen with the roster,
  // exactly as the bottom nav did.
  if (!open) {
    return createPortal(
      <button
        type="button"
        onClick={openPanel}
        aria-label="Find a student by voice"
        /* Clear of the bottom nav on phones, which owns the last 60-odd pixels. */
        className="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 z-40 h-14 w-14 rounded-full bg-blue-600 text-white shadow-lg flex items-center justify-center hover:bg-blue-700 active:scale-95 transition"
      >
        <Mic className="h-6 w-6" />
      </button>,
      document.body,
    );
  }

  return createPortal(
    <div className="fixed inset-x-0 bottom-0 z-50 bg-white border-t border-gray-200 rounded-t-2xl shadow-[0_-8px_30px_rgba(0,0,0,0.12)] pb-safe">
      <div className="max-h-[72vh] overflow-y-auto px-4 pt-3 pb-4 sm:px-6">
        <div className="flex items-center gap-2 mb-2">
          {listening ? (
            <span className="relative flex h-3 w-3" aria-hidden="true">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
            </span>
          ) : (
            <MicOff className="h-4 w-4 text-gray-400" />
          )}
          <h2 className="text-sm font-semibold text-gray-900">
            {listening ? 'Say a student’s name' : supported ? 'Paused' : 'Voice not available'}
          </h2>
          <button
            type="button"
            onClick={closePanel}
            aria-label="Close voice search"
            className="ml-auto p-2 -mr-2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {!supported && (
          <p className="text-sm text-gray-600 mb-3">
            This browser has no speech recogniser. Chrome, or Safari on iOS 14.5 and up, does.
          </p>
        )}
        {error === 'permission' && (
          <p className="text-sm text-amber-700 mb-3">
            The microphone is blocked. Allow it for this site in your browser settings, then
            reopen this panel.
          </p>
        )}
        {error === 'network' && (
          <p className="text-sm text-amber-700 mb-3">
            Recognition needs a connection and could not reach one.
          </p>
        )}
        {error === 'failed' && (
          <p className="text-sm text-amber-700 mb-3">
            The recogniser keeps stopping. Close this and find the student by hand.
          </p>
        )}

        {/* The words the shortlist below came from. A wrong shortlist is only explicable
            next to them. */}
        {supported && (
          <p className="min-h-[1.75rem] text-base text-gray-500 italic mb-2">
            {spoken ? `“${spoken}”` : listening ? 'Listening…' : ''}
          </p>
        )}

        {receipt && <p className="text-sm text-green-700 mb-2">{receipt}</p>}

        {/* Something was heard and it was not a name. The list below is the one the teacher
            already asked for, and it has deliberately not moved. */}
        {missed && shortlist && (
          <p className="text-xs text-amber-700 mb-2">
            That didn’t sound like a name — still showing “{shortlist.transcript}”. Say the next
            name to move on.
          </p>
        )}

        {/* Only once the recogniser has committed. While the words are still in flight this
            says nothing: half a name matches nobody, and a teacher mid-sentence does not
            need telling so. */}
        {heard && !interim && suggestions.length === 0 && (
          <p className="text-sm text-amber-700">
            Nobody on this roster sounds like that. Say it again, or find them by hand.
          </p>
        )}

        {suggestions.length > 0 && (
          <>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
              {suggestions.length === 1 ? 'Best match' : `${suggestions.length} closest matches`}
            </p>
            <ul className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
              {suggestions.map(suggestion => {
                const entry = byEnrollment.get(suggestion.key);
                if (!entry) return null;
                const actions = actionsFor(entry);
                return (
                  <li key={suggestion.key} className="p-3">
                    <div className="flex items-start gap-3">
                      <StudentAvatar
                        name={entry.studentName}
                        photoUrl={photoUrlFor?.(entry) ?? null}
                        size="sm"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                          <p className="font-medium text-gray-900">{entry.studentName}</p>
                          {showAfterSchoolBadge && (
                            <AfterSchoolBadge inAfterSchool={entry.inAfterSchool} />
                          )}
                        </div>
                        <p className="text-[11px] text-gray-500">{describe(entry)}</p>
                      </div>
                      <AttendanceStatusBadge status={statusFor(entry)} />
                    </div>

                    {actions.length > 0 ? (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {actions.map(action => (
                          <button
                            key={action.key}
                            type="button"
                            onClick={() => runAction(entry, action)}
                            className={`px-3 py-2 text-sm font-medium rounded-lg ${action.className}`}
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    ) : (
                      // A Flex child on a day nobody bought. The roster offers nothing until
                      // the freeze is lifted on the row itself, and neither does this.
                      <p className="mt-2 text-[11px] text-amber-700">
                        Nothing can be recorded for {entry.studentName} today — open their row
                        on the roster.
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}

        {!spoken && !receipt && supported && (
          <p className="text-xs text-gray-500">
            Just the name — “Emma Chen”, or “Emma”. The closest few come back to pick from.
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
}
